<template>
  <div class="chat-container" >
    <vue-advanced-chat
      height="calc(100vh - 220px)"
      :current-user-id="currentUserId"
      :rooms="JSON.stringify(rooms)"
      :rooms-loaded="roomsLoaded"
      :messages="JSON.stringify(messages)"
      :messages-loaded="messagesLoaded"
      :show-add-room="false"
      :show-reaction-emojis="false"
      :show-files="false"
      :show-audio="false"
      :show-footer="!isCancelled"
      :message-actions="JSON.stringify([])"
      @send-message="sendMessage"
      @fetch-messages="fetchMessages"
    />
    <div v-if="isCancelled" class="chat-overlay">
      <div class="overlay-message">
        <localized-text localizedKey="Appuntamento Annullato" text="Appuntamento Annullato"></localized-text>
      </div>
    </div>
  </div>
</template>

<script>
import { register } from 'vue-advanced-chat';
import { ChatClient } from '@/services/Services';
import * as signalR from '@microsoft/signalr';
import * as OM from '@/Model';
import userAvatar from '@/assets/img/avatar.svg';

register();

export default {
  props: ['prenotazioneId', 'consulenteImage', 'cancelledOn'],
  data() {
    return {
      currentUserId: null,
      rooms: [],
      messages: [],
      roomsLoaded: false,
      messagesLoaded: false,
      activeRoomId: null,
      signalRConnection: null,
      rawRoomData: null
    };
  },
  computed: {
    isCancelled() {
      return this.cancelledOn && this.cancelledOn !== '';
    }
  },
  methods: {
    formatRoom(rawRoom) {
      return [{
        roomId: rawRoom.identifier,
        roomName: `${rawRoom.consulenteName} `,
        avatar: this.consulenteImage,
        users: rawRoom.userIds.map(id => ({
          _id: id,
          username: id === this.currentUserId ? 'Tu' : 'Consulente',
          avatar: id === this.currentUserId ? userAvatar : this.consulenteImage,
        })),
      }];
    },
    formatMessages(rawMessages) {
        const activeRoom = this.rooms[0];
        if (!activeRoom) return [];
        const userMap = new Map(activeRoom.users.map(u => [u._id, u]));

        return rawMessages.map(message => ({
            _id: message.identifier,
            content: message.content,
            senderId: message.senderId,
            username: userMap.get(message.senderId)?.username || 'Sconosciuto',
            avatar: userMap.get(message.senderId)?.avatar || 'https://placekitten.com/80/80',
            timestamp: new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            date: new Date(message.timestamp).toLocaleDateString(),
        }));
    },
    fetchMessages(event) {
        const room = event.room || event.detail?.[0]?.room;
        if (!room) return;

        if (room.roomId === this.activeRoomId) return;

        this.activeRoomId = room.roomId;
        this.messagesLoaded = false;

        ChatClient.getMessages(room.roomId, false)
            .then(rawMessages => {
                this.messages = this.formatMessages(rawMessages || []);
                ChatClient.markRoomAsRead(room.roomId, false).catch(() => {});
                this.$store.state.getUnreadChatCount();
                this.$nextTick(() => {
                    this.messagesLoaded = true;
                });
            })
            .catch(err => {
                console.error('Errore caricamento messaggi:', err);
                this.messages = [];
                this.$nextTick(() => {
                    this.messagesLoaded = true;
                });
            });
    },
    sendMessage(event) {
      const { roomId, content } = event.detail[0];
      const request = new OM.SendMessageRequest();
      request.content = content;
      ChatClient.sendMessage(roomId, request);
    },
    processRoom(rawRoom) {
      this.rawRoomData = rawRoom;
      this.rooms = this.formatRoom(rawRoom);
      this.roomsLoaded = true;

      if (this.rooms.length > 0) {
        const room = this.rooms[0];

        if (this.signalRConnection && this.signalRConnection.state === 'Connected') {
          this.signalRConnection.invoke('JoinRoom', room.roomId).catch(err => console.error('Errore JoinRoom:', err));
        }

        this.activeRoomId = null;
        this.fetchMessages({ room });
      } else {
        this.messagesLoaded = true;
      }
    }
  },
  watch: {
    consulenteImage(newValue) {
      if (newValue && this.rawRoomData) {
        this.rooms = this.formatRoom(this.rawRoomData);
      }
    }
  },
  mounted() {
    this.currentUserId = this.$store.state.user.identifier;
    const hubUrl = `${process.env.VUE_APP_ENDPOINT}chub`;
    const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
            accessTokenFactory: () => this.$store.state.user.token
        })
        .withAutomaticReconnect()
        .build();

    this.signalRConnection = connection;

    connection.on('ReceiveMessage', (message) => {
        if (message.roomId === this.activeRoomId) {
            this.messages = [...this.messages, ...this.formatMessages([message])];
        }
    });

    const loadChatRoom = () => {
        ChatClient.getRoom(this.prenotazioneId, false)
            .then(rawRoom => {
                if (rawRoom && rawRoom.identifier) {
                    this.processRoom(rawRoom);
                    this.$store.state.activeChatRoomId = rawRoom.identifier;
                } else {
                    this.roomsLoaded = true;
                    this.messagesLoaded = true;
                }
            })
            .catch(chatErr => {
                console.error('Errore caricamento chat:', chatErr);
                this.roomsLoaded = true;
                this.messagesLoaded = true;
            });
    };

    connection.start()
        .then(() => {
            console.log('SignalR connesso');
            if (this.activeRoomId) {
                connection.invoke('JoinRoom', this.activeRoomId).catch(err => console.error('Errore JoinRoom:', err));
            }
        })
        .catch(err => {
            console.error('Errore connessione SignalR:', err);
        });

    loadChatRoom();
  },
  beforeUnmount() {
    this.$store.state.activeChatRoomId = null;
    if (this.signalRConnection) {
      if (this.activeRoomId) {
        this.signalRConnection.invoke('LeaveRoom', this.activeRoomId);
      }
      this.signalRConnection.stop();
    }
  }
};
</script>

<style scoped>
.chat-container {
  width: 100%;
  position: relative;
}

.chat-container :deep(.vac-message-card .vac-format-message-wrapper .markdown p) {
  font-size: 20px !important;
  line-height: 1.4;
}

.chat-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
}

.overlay-message {
  background-color: rgba(220, 53, 69, 0.95);
  color: white;
  padding: 20px 40px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
</style>
